/**
 * 橡皮擦覆盖层
 *
 * 行为：
 *  - 显示随鼠标移动的圆形光标（大小 = eraserSize 画布像素）
 *  - 按下并拖拽时，实时删除光标圆圈范围内的 freehand 笔画元素
 *  - 每帧只处理相交的元素，批量删除（通过 immer patch 产生单条历史记录）
 *  - Esc → 退出橡皮擦工具
 *
 * 碰撞检测：
 *  圆形光标 ∩ 笔画 AABB（轴对齐包围盒）—— "整笔删除"策略，
 *  与专业工具（如 Keynote 演示批注）一致，简单可靠。
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useCanvas } from '@/components/canvas/designer/shell/CanvasContext';

interface Pos { x: number; y: number; }

export interface EraserToolOverlayProps {
  pageWidth: number;
  pageHeight: number;
  zoom: number;
}

export function EraserToolOverlay({ pageWidth, pageHeight, zoom }: EraserToolOverlayProps) {
  const {
    eraserSize,
    getCurrentPage,
    deleteElementsByIds,
    setEraserToolActive,
  } = useCanvas();
  const elements = getCurrentPage()?.elements ?? [];

  const svgRef = useRef<SVGSVGElement>(null);
  const [cursorPos, setCursorPos] = useState<Pos | null>(null);
  const isErasingRef = useRef(false);
  /** 本次拖拽中已删除的元素 id（去重，避免对同一元素多次调用删除） */
  const erasedIdsRef = useRef<Set<string>>(new Set());

  const radius = eraserSize / 2; // 画布像素半径

  const getPos = useCallback((e: React.MouseEvent | MouseEvent): Pos => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: (e.clientX - rect.left) / zoom,
      y: (e.clientY - rect.top) / zoom,
    };
  }, [zoom]);

  // ── Esc 退出 ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setEraserToolActive(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setEraserToolActive]);

  // ── 碰撞检测并删除 ────────────────────────────────────────────────────────

  const eraseAt = useCallback((pos: Pos) => {
    const toDelete: string[] = [];
    for (const el of elements) {
      if (el.type !== 'freehand') continue;
      if (erasedIdsRef.current.has(el.id)) continue;

      // 圆形光标与矩形 AABB 的最近点距离检测
      const cx = Math.max(el.layout.x, Math.min(pos.x, el.layout.x + el.size.width));
      const cy = Math.max(el.layout.y, Math.min(pos.y, el.layout.y + el.size.height));
      const dist = Math.sqrt((cx - pos.x) ** 2 + (cy - pos.y) ** 2);
      if (dist <= radius) {
        toDelete.push(el.id);
        erasedIdsRef.current.add(el.id);
      }
    }
    if (toDelete.length > 0) deleteElementsByIds(toDelete);
  }, [elements, radius, deleteElementsByIds]);

  // ── 鼠标事件 ──────────────────────────────────────────────────────────────

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    isErasingRef.current = true;
    erasedIdsRef.current = new Set();
    const pos = getPos(e);
    setCursorPos(pos);
    eraseAt(pos);
  }, [getPos, eraseAt]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    const pos = getPos(e);
    setCursorPos(pos);
    if (!isErasingRef.current) return;
    eraseAt(pos);
  }, [getPos, eraseAt]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (!isErasingRef.current) return;
    e.preventDefault();
    e.stopPropagation();
    isErasingRef.current = false;
    erasedIdsRef.current = new Set();
  }, []);

  const handleMouseLeave = useCallback(() => {
    setCursorPos(null);
    isErasingRef.current = false;
  }, []);

  // 橡皮擦光标半径（屏幕像素恒定 → 除以 zoom）
  const cursorR = radius / zoom;

  return (
    <svg
     
      ref={svgRef}
      viewBox={`0 0 ${pageWidth} ${pageHeight}`}
      width={pageWidth}
      height={pageHeight}
      style={{
        position: 'absolute',
        inset: 0,
        cursor: 'none', // 自定义圆形光标替代系统光标
        userSelect: 'none',
        zIndex: 1000,
        overflow: 'visible',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseLeave}
    >
      {/* 圆形橡皮擦光标 */}
      {cursorPos && (
        <g>
          <circle
            cx={cursorPos.x}
            cy={cursorPos.y}
            r={cursorR}
            fill={isErasingRef.current ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.2)'}
            stroke={isErasingRef.current ? '#ef4444' : '#666'}
            strokeWidth={1 / zoom}
            strokeDasharray={isErasingRef.current ? 'none' : `${3 / zoom} ${3 / zoom}`}
          />
          {/* 中心十字 */}
          <line
            x1={cursorPos.x - 4 / zoom} y1={cursorPos.y}
            x2={cursorPos.x + 4 / zoom} y2={cursorPos.y}
            stroke={isErasingRef.current ? '#ef4444' : '#666'}
            strokeWidth={1 / zoom}
          />
          <line
            x1={cursorPos.x} y1={cursorPos.y - 4 / zoom}
            x2={cursorPos.x} y2={cursorPos.y + 4 / zoom}
            stroke={isErasingRef.current ? '#ef4444' : '#666'}
            strokeWidth={1 / zoom}
          />
        </g>
      )}
    </svg>
  );
}
